import { IScope, IPromise, IQService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { toIdMap } from "@gtmhub/util";
import { ISessionNode } from "../../../goals/models";
import { TeamsActions } from "@gtmhub/teams";
import { ISessionsIdMap, IPlanningSession, PlanningSessionsActions, ISessionsStoreState } from "@gtmhub/sessions";
import { IAssigneeIdMap, AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { IUser } from "@gtmhub/users/models";
import { UserService, getCurrentUserId } from "@gtmhub/users";
import { UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IStateInit } from "@gtmhub/core/routing";
import { constructSessionsTree } from "../../../goals/utils";
import { AlignmentLoader, AlignmentTreeViewMode, IAlignmentLoadedEventArgs, AlignmentContentState } from "../alignmentLoader";
import { localize } from "@gtmhub/localization";
import {
  IGridFilter,
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridSortingRule,
  toGridDefinitionMap,
  IGridFiltersChange,
  IGoalsSharedViewOptions,
  setPersistedScreenSettings,
  getPersistedScreenSettings,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { goalsFilterDefinitions, generateDefaultFilters, goalsGridPersistedScreenPath, goalsPersistedScreenPath, goalsViewOptionsPath } from "@gtmhub/goals/filter-utils";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountType } from "@gtmhub/core";
import { INgRedux } from "@gtmhub/state-management";
import { ICustomFieldsAllTypesMap, CustomFieldActions, ICustomFieldsStoreState } from "@gtmhub/customFields";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { MISSING_SESSION_ID_STATE_PARAM } from "@gtmhub/goals/controllers/all";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { AuthenticationResolverService } from "@gtmhub/auth";

const alignmentScreenName = "alignment";
const nestedScreenName = "nested";

export type AlignmentViewModes = Record<"nested" | "showMetrics", boolean>;

interface IAlignmentSidebarRedux {
  customFields: ICustomFieldsAllTypesMap;
  assigneesIdMap: IAssigneeIdMap;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
  sessions: IPlanningSession[];
}

export interface IAlignmentSidebarScope extends IExtendedRootScopeService, IAlignmentSidebarRedux, IScope {
  currentUser: IUser;
  enabledDrag: boolean;
  indicators: {
    initial?: boolean;
    searching?: boolean;
  };
  sessionsTree: ISessionNode;
  isDemoAccount: boolean;
  filterDefinitions: GridFilterDefinitions;
  filters: IGridFilter[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  viewModes: AlignmentViewModes;
  // we update alignment-sidebar-ctrl(parent) by reference from alignment-tree-ctrl and alignment-nested-ctrl(children) scope:
  // - receive data with args and assign it to a variable object to keep the reference to the parent
  contentState: AlignmentContentState;
  sessions: IPlanningSession[];
  sortingChange(): void;
  filtersChange(change: IGridFiltersChange): void;
  collapseAll(): void;
  expandAll(): void;
  changeViewMode(): void;
  toggleIncludeKeyResults(): void;
  switchDragMode(): void;
  logout(): void;
}

export interface IAlignmentSidebarStateParamsService {
  planningSessionId?: string;
  sharedId?: string;
  sharedParentId?: string;
  locateId?: string;
}

const bindStateToScope = (state: IAssigneesStoreState & ICustomFieldsStoreState & ISessionsStoreState): IAlignmentSidebarRedux => ({
  assigneesIdMap: state.assignees.map,
  customFields: state.customFields.map,
  reduxItemsFetched: state.assignees.isFetched && state.customFields.isFetched && state.sessions.isFetched,
  reduxItemsError: state.assignees.error || state.customFields.error || state.sessions.error,
  sessions: state.sessions.items,
});

export class AlignmentSidebarCtrl implements IStateInit {
  private sessionIdMap: ISessionsIdMap;
  private currentSessionId: string;
  private isDesignScoreGridEnabled = false;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$q",
    "AlignmentLoader",
    "CurrentUserService",
    "UserService",
    "AccountResolverService",
    "UIErrorHandlingService",
    "AssigneeActions",
    "CustomFieldActions",
    "$ngRedux",
    "TeamsActions",
    "PlanningSessionsActions",
    "FeatureToggleV2Service",
    "AuthenticationResolverService",
  ];

  constructor(
    private $rootScope: ITraceRootScopeService,
    private $scope: IAlignmentSidebarScope,
    private $state: StateService,
    private $stateParams: IAlignmentSidebarStateParamsService,
    private $q: IQService,
    private alignmentLoader: AlignmentLoader,
    private currentUserService: CurrentUserService,
    private userService: UserService,
    private accountResolverService: AccountResolverService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private customFieldActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private planningSessionsActions: PlanningSessionsActions,
    private featureToggleService: FeatureToggleV2Service,
    private authenticationResolverService: AuthenticationResolverService
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$scope.collapseAll = this.collapseAll;
    this.$scope.expandAll = this.expandAll;
    this.$scope.switchDragMode = this.switchDragMode;
    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.changeViewMode = this.changeViewMode;
    this.$scope.toggleIncludeKeyResults = this.toggleIncludeKeyResults;
    this.$scope.logout = this.logout;
    this.$scope.contentState = { hasItems: false, noFiltersOrHostSessionOnly: true };
    this.$scope.indicators = {};

    const account = this.accountResolverService.getAccountData();
    this.$scope.isDemoAccount = account.type === AccountType.DemoAccount;

    this.alignmentLoader.init();
  }

  public stateInit(): IPromise<unknown> {
    if (this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM) {
      this.$state.go("gtmhub.goals.all", { planningSessionId: this.$stateParams.planningSessionId });
    } else {
      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
      this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
      this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

      return this.init();
    }
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators.initial = true;
    this.setDragOption();

    return this.$q
      .all({
        currentUser: this.userService.getUser(getCurrentUserId()),
        isDesignScoreGridEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.DesignScoreGrid),
        _: this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.reduxItemsFetched,
          () => this.$scope.reduxItemsError
        ),
      })
      .then(
        (map) => {
          // keeping this assignment before indicators are turned off in order to show/hide correspoding html before revealing it

          this.$scope.indicators.initial = false;
          this.$scope.currentUser = map.currentUser;
          this.isDesignScoreGridEnabled = map.isDesignScoreGridEnabled;

          const sortedSessions = this.$scope.sessions.sort((a: IPlanningSession, b: IPlanningSession) => b.end.localeCompare(a.end));
          this.initSessions(sortedSessions);

          this.setViewModes();
          this.initUnifiedFilters();
        },
        (err) => this.uiErrorHandlingService.handleModal(err)
      );
  };

  private constructAlignmentViewArgsFromFilters = (): IAlignmentLoadedEventArgs => {
    return {
      currentUser: this.$scope.currentUser,
      assigneesIdMap: this.$scope.assigneesIdMap,
      sessionIdMap: this.sessionIdMap,
      currentSessionId: this.currentSessionId,
      sharedId: this.alignmentLoader.isResolved ? undefined : this.$stateParams.sharedId,
      sharedParentId: this.alignmentLoader.isResolved ? undefined : this.$stateParams.sharedParentId,
      locateId: this.alignmentLoader.isResolved ? null : this.$stateParams.locateId,
      viewModes: this.$scope.viewModes,
      enabledDrag: this.$scope.enabledDrag,
      order: this.$scope.sortingRules,
      filters: this.$scope.filters,
      contentState: this.$scope.contentState,
    };
  };

  private determineViewMode = (): "nested" | "relaxed" | "medium" => {
    if (this.$scope.viewModes.nested) {
      return "nested";
    } else {
      return this.$scope.viewModes.showMetrics ? "relaxed" : "medium";
    }
  };

  private setViewModes = (): void => {
    const viewModeFromUserSettings: AlignmentTreeViewMode = this.currentUserService.getUserSetting("alignmentTreeMode");
    const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};

    // TODO: Delete with https://gtmhub.atlassian.net/browse/GVS-6367
    const shouldMigrateSettingsFromUserSettings =
      viewModeFromUserSettings && typeof gridViewOptions.isNestedView === "undefined" && typeof gridViewOptions.includeKeyResults === "undefined";

    if (shouldMigrateSettingsFromUserSettings) {
      switch (viewModeFromUserSettings) {
        case "medium":
        case "compact":
          {
            gridViewOptions.isNestedView = false;
            gridViewOptions.includeKeyResults = false;
          }
          break;
        case "relaxed":
          {
            gridViewOptions.isNestedView = false;
            gridViewOptions.includeKeyResults = true;
          }
          break;
        case "nested":
          {
            gridViewOptions.isNestedView = true;
            gridViewOptions.includeKeyResults = false;
          }
          break;
      }

      setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", gridViewOptions);
    }

    this.$scope.viewModes = {
      nested: !!gridViewOptions.isNestedView && !this.$stateParams.locateId && !this.$stateParams.sharedId,
      showMetrics: gridViewOptions.includeKeyResults == null ? true : gridViewOptions.includeKeyResults,
    };
  };

  private setDragOption = (): void => {
    const persistedViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};

    if (persistedViewOptions) {
      this.$scope.enabledDrag = !!persistedViewOptions.useDragMode;
    } else {
      this.$scope.enabledDrag = false;
    }
  };

  private initSessions = (sessions: IPlanningSession[]): void => {
    this.sessionIdMap = toIdMap(sessions);
    this.currentSessionId = this.$stateParams.planningSessionId;

    this.$scope.sessionsTree = constructSessionsTree(this.currentSessionId, this.sessionIdMap);
  };

  private switchDragMode = (): void => {
    this.$rootScope.traceAction("switch_drag_mode", () => {
      this.$scope.enabledDrag = !this.$scope.enabledDrag;
      const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
      gridViewOptions.useDragMode = this.$scope.enabledDrag;
      setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", gridViewOptions);
      this.$scope.$broadcast("alignment.dragModeChanged", this.$scope.enabledDrag);
    });
  };

  private expandAll = (): void => {
    this.$rootScope.traceAction("expand_all", () => {
      this.$scope.$broadcast("alignment.expandAll");
    });
  };

  private collapseAll = (): void => {
    this.$rootScope.traceAction("collapse_all", () => {
      this.$scope.$broadcast("alignment.collapseAll");
    });
  };

  private changeViewMode = (): void => {
    // selecting current view is disabled in the UI so we can only choose the not currently selected option
    this.$scope.viewModes.nested = !this.$scope.viewModes.nested;
    const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
    gridViewOptions.isNestedView = this.$scope.viewModes.nested;
    setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", gridViewOptions);

    this.$rootScope.traceAction("switch_view_mode", () => {
      let newState: "gtmhub.goals.alignmentSidebar.nested" | "gtmhub.goals.alignmentSidebar.tree";
      if (this.$scope.viewModes.nested) {
        newState = "gtmhub.goals.alignmentSidebar.nested";
      } else {
        newState = "gtmhub.goals.alignmentSidebar.tree";
      }

      const viewMode = this.determineViewMode();
      this.$scope.$broadcast("alignment.viewModeSwitched", viewMode);

      this.alignmentLoader.init();

      this.$state.go(newState).then(() => {
        this.initUnifiedFilters();
      });
    });
  };

  private toggleIncludeKeyResults = (): void => {
    const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
    gridViewOptions.includeKeyResults = this.$scope.viewModes.showMetrics;
    setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", gridViewOptions);

    if (this.$scope.viewModes.nested) {
      this.$rootScope.$broadcast("nested.viewModeChanged", this.$scope.viewModes.showMetrics);
    } else {
      this.$rootScope.traceAction("switch_view_mode", () => {
        const viewMode = this.determineViewMode();
        this.$scope.$broadcast("alignment.viewModeSwitched", viewMode);
      });
    }
  };

  private screenFiltersPath = () => goalsGridPersistedScreenPath(this.$stateParams.planningSessionId);
  private screenSortingsPath = () => goalsPersistedScreenPath(this.$stateParams.planningSessionId, this.determineScreenName());

  private initUnifiedFilters = (): void => {
    if (this.isDesignScoreGridEnabled) {
      this.$scope.sortingDefinitions = toGridDefinitionMap([
        { fieldName: "assignee.name", displayName: localize("owner_cap"), trackingName: "OKR owner" },
        { fieldName: "name", displayName: localize("objective_name"), trackingName: "OKR name" },
        { fieldName: "attainment", displayName: localize("progress_cap"), trackingName: "OKR progress" },
        { fieldName: "designScore.totalPoints", displayName: localize("okr-design-score"), trackingName: "OKR-design score" },
      ]);
    } else {
      this.$scope.sortingDefinitions = toGridDefinitionMap([
        { fieldName: "assignee.name", displayName: localize("owner_cap"), trackingName: "OKR owner" },
        { fieldName: "name", displayName: localize("objective_name"), trackingName: "OKR name" },
        { fieldName: "attainment", displayName: localize("progress_cap"), trackingName: "OKR progress" },
      ]);
    }

    this.$scope.sortingRules = getPersistedScreenSettings(this.screenSortingsPath(), "sorting") || [{ fieldName: "name", reversed: false }];

    // keeping filters initialisation last as they trigger filtersChange and broadcast args
    this.$scope.filterDefinitions = goalsFilterDefinitions({
      isNestedView: this.$scope.viewModes.nested,
      customFields: this.$scope.customFields,
      planningSessionId: this.$stateParams.planningSessionId,
      isDesignScoreGridEnabled: this.isDesignScoreGridEnabled,
    });

    const createSessionFilter = (): IGridFilter => ({
      fieldName: "sessionId",
      dataType: "array",
      operator: "isOneOf",
      value: [this.$stateParams.planningSessionId],
    });

    const persistedFilters = getPersistedScreenSettings(this.screenFiltersPath(), "filters");
    if (persistedFilters) {
      let filters = persistedFilters;

      // if no session filter is persisted, add session filter at first place
      const sessionFilterIndex = filters.findIndex((filter) => filter.fieldName === "sessionId");
      if (sessionFilterIndex < 0) {
        filters = [createSessionFilter(), ...persistedFilters];
      } else if (this.$scope.viewModes.nested) {
        // in nested view, we do not want to use the persisted sessionId filter since it is readonly
        filters[sessionFilterIndex] = createSessionFilter();
      }

      this.$scope.filters = filters;
    } else {
      this.$scope.filters = generateDefaultFilters(this.$stateParams.planningSessionId);
    }
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    this.$rootScope.traceAction("change_filters", () => {
      if (!change.initial) {
        let filtersToPersist = this.$scope.filters;
        if (this.$scope.viewModes.nested) {
          // If there is a persisted session filter, we have to keep it, since the user might
          // need it on the grid / tree screens
          filtersToPersist = this.$scope.filters.filter((f) => f.fieldName !== "sessionId");

          const persistedFilters = getPersistedScreenSettings(this.screenFiltersPath(), "filters") || [];
          const persistedSessionFilter = persistedFilters.find((f) => f.fieldName === "sessionId");
          if (persistedSessionFilter) {
            filtersToPersist = [persistedSessionFilter, ...filtersToPersist];
          }
        }
        setPersistedScreenSettings(this.screenFiltersPath(), "filters", filtersToPersist);
      }

      const args = this.constructAlignmentViewArgsFromFilters();
      const isAlignmentLoaderResolved = this.alignmentLoader.isResolved;

      const stateParams = { ...this.$stateParams };
      if (!isAlignmentLoaderResolved) {
        this.alignmentLoader.resolve(args);
        // removing sharedId from state params as it won't be needed anymore
        stateParams.sharedId = undefined;
        stateParams.sharedParentId = undefined;
        this.$state.go(".", stateParams, { notify: false });
      } else {
        this.$rootScope.$broadcast("alignment.unifiedFiltersChanged", args);
      }
    });
  };

  private sortingChange = (): void => {
    this.$rootScope.traceAction("change_sorting", () => {
      setPersistedScreenSettings(this.screenSortingsPath(), "sorting", this.$scope.sortingRules);

      const broadcastArgs: Record<"order", IGridSortingRule[]> = {
        order: this.$scope.sortingRules,
      };
      this.$scope.$broadcast("alignment.orderChanged", broadcastArgs);
    });
  };

  private determineScreenName = (): "alignment" | "nested" => {
    return this.$scope.viewModes.nested ? nestedScreenName : alignmentScreenName;
  };

  private logout = (): void => {
    this.authenticationResolverService.logout({ ssoLogoutIfEnabled: true });
  };
}
